import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router";
import $api from "./openapi";
import { components } from "../ApiSchema";

function replaceOperationInPart(
  part: components["schemas"]["RequestedPartWithOperationsSchema"],
  updatedOperation: components["schemas"]["OperationSummarySchema"],
) {
  const partHasOperation = part.operations.find(
    ({ id }) => id === updatedOperation.id,
  );

  if (partHasOperation) {
    return {
      ...part,
      operations: part.operations.map((op) =>
        op.id === updatedOperation.id ? updatedOperation : op,
      ),
    };
  }

  return undefined;
}

function getUpdatedPartOrSubPart(
  part: components["schemas"]["RequestedPartWithOperationsSchema"],
  updatedOperation: components["schemas"]["OperationSummarySchema"],
) {
  const updatedPart = replaceOperationInPart(part, updatedOperation);
  if (updatedPart) {
    return updatedPart;
  }

  for (const subPart of part.children) {
    const updatedSubPart = replaceOperationInPart(subPart, updatedOperation);
    if (updatedSubPart) {
      return {
        ...part,
        children: part.children.map((sp) =>
          sp.id === updatedSubPart.id ? updatedSubPart : sp,
        ),
      };
    }
  }
}

function getQueryKey(teamSlug: string, partId: string) {
  return [
    "get",
    "/api/teams/{team_slug}/requested_parts/{id}",
    {
      params: {
        path: {
          team_slug: teamSlug,
          id: partId,
        },
      },
    },
  ];
}

export default function useUpdateOperation() {
  const { teamSlug, partId } = useParams();
  const queryClient = useQueryClient();

  const { mutate } = $api.useMutation(
    "patch",
    "/api/teams/{team_slug}/operations/{id}",
  );

  return (operationId: string, draft: Record<string, never>) =>
    mutate(
      {
        params: {
          path: { team_slug: teamSlug!, id: operationId },
        },
        body: {
          draft,
        },
      },
      {
        onSuccess: (updatedOperation) => {
          queryClient.setQueryData(
            getQueryKey(teamSlug!, partId!),
            (
              part: components["schemas"]["RequestedPartWithOperationsSchema"],
            ) => {
              for (const subPart of part.children) {
                queryClient.setQueryData(
                  getQueryKey(teamSlug!, subPart.id),
                  (
                    childPart: components["schemas"]["RequestedPartWithOperationsSchema"],
                  ) => {
                    if (!childPart) return;
                    return getUpdatedPartOrSubPart(childPart, updatedOperation);
                  },
                );
              }

              if (part.parent) {
                queryClient.setQueryData(
                  getQueryKey(teamSlug!, part.parent.id),
                  (
                    parent: components["schemas"]["RequestedPartWithOperationsSchema"],
                  ) => {
                    if (!parent) return;
                    return getUpdatedPartOrSubPart(parent, updatedOperation);
                  },
                );
              }

              return getUpdatedPartOrSubPart(part, updatedOperation);
            },
          );
        },
      },
    );
}

export function useMoveOperation() {
  const queryClient = useQueryClient();
  const { teamSlug, partId } = useParams();

  return $api.useMutation("patch", "/api/teams/{team_slug}/operations/{id}", {
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          "get",
          "/api/teams/{team_slug}/requested_parts/{id}",
          {
            params: {
              path: {
                team_slug: teamSlug!,
                id: partId!,
              },
            },
          },
        ],
      });
    },
  });
}
